import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppShell } from '@/components/AppShell';
import { useFreshness, useLogout, useMe, useOperation, useUnreadCount } from '@/api/hooks';
import { freshnessLabel } from '@/lib/freshness';
import { PAGE_TITLES } from '@/lib/nav';
import { LoginScreen } from '@/screens/LoginScreen';
import { OverviewScreen } from '@/screens/OverviewScreen';
import { FinanceScreen } from '@/screens/FinanceScreen';
import { WalletsScreen } from '@/screens/WalletsScreen';
import { WalletDetailScreen } from '@/screens/WalletDetailScreen';
import { ServiceDetailScreen } from '@/screens/ServiceDetailScreen';
import { TasksScreen } from '@/screens/TasksScreen';
import { NotificationsScreen } from '@/screens/NotificationsScreen';
import { SettingsScreen } from '@/screens/SettingsScreen';

function Booting({ message }: { message: string }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        font: '400 14px var(--font-text)',
        color: 'var(--text-subtle)',
      }}
    >
      {message}
    </div>
  );
}

export function App() {
  const me = useMe();
  const logout = useLogout();
  const queryClient = useQueryClient();
  const location = useLocation();

  const [refreshing, setRefreshing] = useState(false);
  const [, setTick] = useState(0);

  // Подпись «обновлено N минут назад» должна стареть сама, без перезагрузки.
  useEffect(() => {
    const timer = window.setInterval(() => setTick((t) => t + 1), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  // Кнопка «Обновить» запускает сбор данных немедленно, но не чаще раза
  // в минуту (docs/11-nonfunctional.md). Если провайдеры уже опрашивались,
  // просто перечитываем то, что есть: отказ ради отказа тут не нужен.
  const sync = useOperation<Record<string, never>, unknown>('sync.run');

  // Значок непрочитанных виден на каждом экране, поэтому счётчик живёт
  // в каркасе, а не на экране уведомлений.
  const unread = useUnreadCount(Boolean(me.data?.user));

  // Подпись в шапке говорит о возрасте данных, а не о времени открытия
  // страницы: иначе приложение хвалится свежестью, которой нет.
  const freshness = useFreshness(Boolean(me.data?.user));

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      try {
        await sync.mutateAsync({});
      } catch {
        // Пауза между запусками или отсутствие сервисов не должны мешать
        // обновить экран.
      }
      await queryClient.invalidateQueries();
    } finally {
      setRefreshing(false);
    }
  }, [queryClient, sync]);

  if (me.isLoading) return <Booting message="Открываем" />;

  if (me.isError) {
    return <Booting message="Сервер не отвечает. Проверьте, что он запущен" />;
  }

  const user = me.data?.user ?? null;
  if (!user) return <LoginScreen googleEnabled={me.data?.googleEnabled ?? false} />;

  const basePath = `/${location.pathname.split('/')[1] ?? ''}`;
  const title = PAGE_TITLES[location.pathname] ?? PAGE_TITLES[basePath] ?? 'Обзор';

  return (
    <AppShell
      title={title}
      subtitle={freshnessLabel(freshness.data)}
      unreadCount={unread.data?.count ?? 0}
      onRefresh={() => void refresh()}
      refreshing={refreshing}
      user={user}
      onLogout={() => logout.mutate()}
      loggingOut={logout.isPending}
    >
      <Routes>
        <Route path="/" element={<OverviewScreen />} />
        <Route path="/finance" element={<FinanceScreen />} />
        <Route path="/finance/:id" element={<ServiceDetailScreen />} />
        <Route path="/wallets" element={<WalletsScreen />} />
        <Route path="/wallets/:id" element={<WalletDetailScreen />} />
        <Route path="/tasks" element={<TasksScreen />} />
        <Route path="/notifications" element={<NotificationsScreen />} />
        <Route path="/settings" element={<SettingsScreen user={user} />} />
        <Route path="/settings/:tab" element={<SettingsScreen user={user} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
